<template>
  <div
    class="public-user-name"
    :class="[`${layout}-layout`, type, { editable: canEdit }]"
  >
    <PublicEmojiEditorPopup
      v-if="canEdit && emoji"
      @close="showEmojiPopup = false"
      @open="showEmojiPopup = true"
      @emojiSaved="emojiSaved"
      :show="showEmojiPopup"
      :emoji="emoji"
    />

    <div
      class="emoji-wrapper"
      :role="canEdit ? 'button' : false"
      :aria-haspopup="canEdit"
      @click.stop="emojiClicked"
    >
      <PublicUserEmoji v-if="emoji" :emoji="emoji" :glow="glow" />
    </div>

    <PublicUsernameEditorPopup
      v-if="canEdit"
      @close="showWordsPopup = false"
      @open="showWordsPopup = true"
      @usernameSaved="usernameSaved"
      :show="showWordsPopup"
      :word-one-data="firstWordData"
      :word-two-data="secondWordData"
    />

    <div
      class="text-wrapper"
      :role="canEdit ? 'button' : false"
      :aria-haspopup="canEdit"
      @click.stop="editUserName"
    >
      <LoadingOverlay :size="1" v-if="isLoading" />

      <p
        class="user-name-text"
        :class="{ 'can-edit': canEdit }"
        :title="userNameText"
      >
        {{ isCompact ? usernameInitials : userNameText }}
        <FaIcon v-if="canEdit" icon="pen" type="regular" />
      </p>
      <slot />
    </div>
  </div>
</template>

<script>
import LoadingOverlay from '../../LoadingOverlay'
import PublicUserEmoji from './PublicUserEmoji'

import FaIcon from '../../ui/FaIcon'
import PublicUsernameEditorPopup from '../username-editor/PublicUsernameEditorPopup'
import PublicEmojiEditorPopup from '../username-editor/PublicEmojiEditorPopup'

export default {
  name: 'PublicUserName',
  components: {
    PublicEmojiEditorPopup,
    PublicUsernameEditorPopup,
    FaIcon,
    LoadingOverlay,
    PublicUserEmoji,
  },
  props: {
    firstWordData: { type: Object, required: true },
    secondWordData: { type: Object, required: true },
    emoji: String,
    glow: {
      type: Boolean,
      default: false,
    },
    type: String,
    canEdit: Boolean,
    layout: {
      type: String,
      validator: type => {
        return ['inline', 'stacked', 'compact'].includes(type)
      },
      default: 'stacked',
    },
    isLoading: Boolean,
  },
  data() {
    return {
      showWordsPopup: false,
      showEmojiPopup: false,
    }
  },
  computed: {
    isCompact() {
      return this.layout.includes('compact')
    },
    userNameText() {
      return (
        (this.firstWordData['word'] || '') +
        // This looks like an empty string, but it's really a zero width space used for breaking things up when it gets too narrow
        '​' +
        (this.secondWordData['word'] || '')
      )
    },
    usernameInitials() {
      const firstWord = this.firstWordData['word']
        ? this.firstWordData['word'][0]
        : ''
      const secondWord = this.secondWordData['word']
        ? this.secondWordData['word'][0]
        : ''

      return firstWord + secondWord
    },
  },
  methods: {
    editUserName() {
      if (this.canEdit) {
        this.showWordsPopup = !this.showWordsPopup
      }
    },
    emojiClicked() {
      if (this.canEdit) {
        this.showEmojiPopup = !this.showEmojiPopup
      }
    },
    /**
     * @param {string} emoji
     */
    emojiSaved(emoji) {
      this.showEmojiPopup = false
      this.$emit('emojiSaved', emoji)
    },
    /**
     * @param {string} wordOne
     * @param {string} wordTwo
     */
    usernameSaved(wordOne, wordTwo) {
      this.showWordsPopup = false
      this.$emit('usernameSaved', wordOne, wordTwo)
    },
  },
}
</script>

<style lang="scss" scoped>
.public-user-name {
  $deepPurple: #6c0073;
  $blue: #103b7a;
  $lightenValue: 50%;

  max-width: 100%;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;

  .emoji-wrapper {
    display: inline-block;

    vertical-align: middle;
    margin-right: 0.3em;
    // Add invisible border radius to make sure the click target doesn't overflow the visual button
    border-radius: 100%;
  }

  .emoji {
    font-size: 3em;
  }

  .text-wrapper {
    position: relative;
    display: inline-block;
    max-width: 100%;
    box-sizing: border-box;
    vertical-align: middle;
    padding: 0.25em 0.75em;
  }

  .user-name-text {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: bold;

    &.can-edit {
      padding-right: 1.25em;
    }

    .fa-icon {
      position: absolute;
      right: 0.5em;
      top: 0.5em;
    }
  }

  // Layouts
  &.stacked-layout,
  &.compact-layout {
    .text-wrapper {
      border-radius: 0.5em;
      background: $white;
      box-shadow: $mainBoxShadow;
      // Make space for the box shadow so that it's not clipped by `overflow: hidden;` on the parent element
      margin-bottom: 0.6em;
    }
    .emoji-wrapper {
      display: block;
      margin: 0 0 0.5em;
    }

    .emoji {
      margin: auto;
    }
  }

  &.inline-layout {
    display: inline-flex;
    align-items: center;

    .text-wrapper {
      text-align: left;
    }
    .emoji {
      --emoji-circle-size: 1.5em;
      --emoji-glow-size: 2.75em;

      font-size: 1.5em;
    }
  }

  &.compact-layout {
    .text-wrapper {
      margin-top: -1.5em;
    }

    .emoji {
      --emoji-circle-size: 1.75em;
      --emoji-glow-size: 3em;
    }
  }
}
</style>
